import { useAppContext } from "@contexts";
import { DropdownMenu } from "@controls";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { useOrganizationContext } from "@src/modules/member/context/organization-context/hook";
import { useLogout, useMe } from "@src/modules/member/hooks/me";
import {
  useOrganization,
  useOrganizations,
} from "@src/modules/member/hooks/organizations";
import { useMemberPath } from "@src/modules/member/hooks/use-member-path";
import { useHasPermission } from "@src/modules/member/hooks/use-permissions";
import type { Organization } from "@src/modules/member/models/organization";
import {
  INVOICE_SETTINGS_PATH,
  NEW_ORGANIZATION_PATH,
  ORGANIZATION_PATH,
  PROFILE_PATH,
} from "@src/modules/member/modules/settings/router/paths";
import { toMemberPath } from "@src/modules/member/path";
import classNames from "classnames";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function OrganizationImage({ organization }: { organization?: Organization }) {
  if (!organization?.name) {
    return null;
  }

  if (organization.image_url) {
    return (
      <img
        src={organization.image_url}
        alt={organization.name}
        className="w-10 rounded"
      />
    );
  }

  return <div>{organization.name[0].toUpperCase()}</div>;
}

interface OrganizationProps {
  minimized: boolean;
}

function Organization({ minimized }: OrganizationProps) {
  const { t } = useTranslation();

  const { setSwitchOrganizationDialogIsOpen } = useAppContext();
  const { currentOrganizationCode } = useOrganizationContext();
  const { settingsPath } = useMemberPath();
  const navigate = useNavigate();
  const {
    data: organizationsResponse,
    isLoading: organizationsIsLoading,
    dataUpdatedAt: organizationsUpdatedAt,
  } = useOrganizations();
  const { data: organizationResponse } = useOrganization(
    currentOrganizationCode as string,
  );

  useEffect(() => {
    if (!organizationsIsLoading && organizationsResponse?.data?.length === 0) {
      navigate(toMemberPath(NEW_ORGANIZATION_PATH()));
    }
  }, [organizationsUpdatedAt]);

  const { mutate: logout } = useLogout();

  const { data: meResponse, isLoading: meIsLoading } = useMe();
  const me = meResponse?.data ?? { email: "" };

  const organization = organizationResponse?.data;

  const cantDisplayOrganizations = !organization || meIsLoading;
  if (cantDisplayOrganizations) {
    return (
      <div
        className={classNames(
          "border-gray-200 bg-white rounded-lg w-full h-fit p-2 flex gap-2",
        )}
      >
        <div className="h-10 w-10 bg-gray-200 rounded" />
        {!minimized && (
          <div className="flex-grow flex flex-col gap-1">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-[12px] w-2/3 bg-gray-200 rounded" />
          </div>
        )}
      </div>
    );
  }

  const handleLogout = () => {
    logout(null);
  };

  const canUpdateOrg = useHasPermission(PERMISSIONS.ORGANIZATION_UPDATE);
  const canViewInvoices = useHasPermission(PERMISSIONS.INVOICES_VIEW_LIST);

  const settingsActions = [
    ...(canUpdateOrg
      ? [
          {
            key: "organization",
            label: t("organizationSettings"),
            onClick: () => navigate(settingsPath(ORGANIZATION_PATH())),
          },
        ]
      : []),
    {
      key: "profile",
      label: t("profileSettings"),
      onClick: () => navigate(settingsPath(PROFILE_PATH())),
    },
    ...(canViewInvoices
      ? [
          {
            key: "invoiceSettings",
            label: t("member.settings.invoices.invoiceSettings"),
            onClick: () => navigate(settingsPath(INVOICE_SETTINGS_PATH())),
          },
        ]
      : []),
  ];

  const menuActions = [
    {
      key: "settings",
      actions: settingsActions,
    },
    {
      key: "others",
      actions: [
        {
          key: "switchOrganization",
          label: t("switchOrganization"),
          onClick: () => setSwitchOrganizationDialogIsOpen(true),
        },
        {
          key: "logout",
          label: t("logout"),
          onClick: handleLogout,
        },
      ],
    },
  ];

  return (
    <DropdownMenu
      triggerChild={
        <div
          className={classNames(
            "border-gray-200 bg-white rounded-lg",
            "p-2 flex",
            "w-full h-fit",
          )}
        >
          <div className="h-10 min-w-10 bg-white rounded flex justify-center items-center text-lg font-bold">
            <OrganizationImage organization={organization} />
          </div>
          {!minimized && (
            <div className="text-gray-900 flex flex-grow flex-col justify-center min-w-0 max-w-full text-start ml-2">
              <div className="text-sm overflow-hidden whitespace-nowrap font-semibold my-0">
                {organization.name}
              </div>
              <span className="my-0 font-light text-xs">{me.email}</span>
            </div>
          )}
        </div>
      }
      ariaLabel="change organization"
      groups={menuActions}
    />
  );
}

export default Organization;
